import React from 'react';
import { Award, TrendingUp, Users, DollarSign, BookOpen, Shield } from 'lucide-react';

const StrategicAccomplishments = () => {
  const accomplishments = [
    {
      icon: <TrendingUp size={40} />,
      metric: 'Multi-Site Leadership',
      value: '3 Schools',
      description: 'Successfully led operations across three bilingual primary school sites in Panama. Managed all stakeholder communications in both English and Spanish, adapted the British Curriculum for effective bilingual delivery, and implemented unified safeguarding frameworks, curriculum standards, and staff development programs while respecting site-specific contexts.',
      impact: 'Enhanced educational outcomes and operational efficiency across all bilingual sites'
    },
    {
      icon: <Users size={40} />,
      metric: 'Team Leadership',
      value: '100+ Staff',
      description: 'Built and managed high-performing teaching and support staff teams across multiple sites, implementing professional development programs, mentorship structures, and performance management systems in bilingual educational environments.',
      impact: 'Improved staff retention and teaching quality across primary phases'
    },
    {
      icon: <Shield size={40} />,
      metric: 'Safeguarding Excellence',
      value: 'DSL Leadership',
      description: 'Served as Designated Safeguarding Lead across multiple contexts, developing comprehensive child protection frameworks and training protocols. Led and implemented an internet-based Safeguarding platform ensuring good practice, continuity of information, and multi-agency collaboration systems that exceeded regulatory requirements.',
      impact: 'All child protection issues dealt with effectively and promptly; embedded culture of safety with robust digital systems ensuring accountability'
    },
    {
      icon: <BookOpen size={40} />,
      metric: 'SEND/Inclusion Leadership',
      value: '100+ Students',
      description: 'Established and led Resource Base serving over 100 students with complex SEND and SEMH needs, developing evidence-based intervention programs and transition pathways to mainstream education.',
      impact: '85%+ successful mainstream reintegration rate for students at risk of permanent exclusion'
    },
    {
      icon: <Award size={40} />,
      metric: 'Strategic Panel Leadership',
      value: '9 Years',
      description: 'Appointed to high-level decision-making bodies (BAC and SENAG) evaluating applications, determining funding allocations, and shaping local authority SEND policy—interpreting complex legal frameworks to ensure equitable, lawful provision.',
      impact: 'Influenced policy affecting 1000+ children across multiple schools'
    },
    {
      icon: <DollarSign size={40} />,
      metric: 'Budget & Resource Management',
      value: 'Multi-Department',
      description: 'Managed departmental budgets for curriculum resources, specialist provisions, and staffing. Optimised resource allocation to maximise educational impact while maintaining financial accountability.',
      impact: 'Achieved budget efficiency while expanding provision and improving outcomes'
    }
  ];

  const achievements = [
    {
      category: 'Government-Level Impact',
      items: [
        'Redesigned government SEMH documentation systems for the Cayman Islands Department of Education, creating more effective reporting frameworks for all stakeholders',
        'Collaborated with Department of Education to develop evidence-based behavioural intervention strategies now used across government schools'
      ]
    },
    {
      category: 'School Improvement Leadership',
      items: [
        'Directed curriculum development and teaching standards across Key Stage 2, ensuring alignment with national frameworks and international best practices',
        'Implemented robust assessment and tracking systems using data analysis to identify trends, drive targeted interventions, and evidence measurable improvement',
        'Led Key Stage 2 team to consistent "Good" and "Outstanding" ratings through strategic focus on teaching quality and student outcomes'
      ]
    },
    {
      category: 'Innovation & System Development',
      items: [
        'Pioneered school\'s inaugural residential program, promoting student independence, risk-taking in safe environments, and community engagement',
        'Developed comprehensive SEND assessment frameworks and intervention protocols adopted across multiple schools in the local authority',
        'Created behavior management CPD programs delivered to teaching staff across the region'
      ]
    },
    {
      category: 'Community & Crisis Leadership',
      items: [
        'Led whole-school response to COVID-19 pandemic, ensuring continuity of learning, safeguarding, and nutritional support for 110+ vulnerable families',
        'Liaised with the University of Panama and AVACA to create the first Health Day for local families, bringing medical experts from all areas of healthcare to provide free health checks within the school for more than 100 families and a local orphanage',
        'Established after-school club for children from the local community (non-enrolled students), teaching English, mathematics, phonics, and computing in English with support from AVACA and volunteer teachers—extending educational access beyond school enrollment',
        'Raised £11,000 for Meals and More charity through 1,300-mile solo cycling challenge, demonstrating leadership through personal commitment',
        'Coordinated multi-partner initiative delivering Christmas support to 250+ families across four schools and community organisations'
      ]
    }
  ];

  return (
    <section id="strategic-accomplishments" className="accomplishments-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Strategic Leadership & Impact</h2>
          <p className="section-subtitle">Evidence of school improvement, system leadership, and measurable outcomes across diverse educational contexts</p>
        </div>

        <div className="metrics-grid">
          {accomplishments.map((item, index) => (
            <div key={index} className="metric-card">
              <div className="metric-icon">{item.icon}</div>
              <div className="metric-content">
                <span className="metric-label">{item.metric}</span>
                <span className="metric-value">{item.value}</span>
                <p className="metric-description">{item.description}</p>
                <div className="metric-impact">
                  <strong>Impact:</strong> {item.impact}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="achievements-section-content">
          <h3>Key Achievements by Domain</h3>
          {achievements.map((category, index) => (
            <div key={index} className="achievement-category">
              <h4>{category.category}</h4>
              <ul className="achievement-list">
                {category.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StrategicAccomplishments;
